import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { FaMapMarkerAlt, FaClock, FaUser, FaEye } from 'react-icons/fa';

const AdminTrips = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });

  useEffect(() => {
    fetchTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.current]);

  const fetchTrips = async () => {
    try {
      const params = {
        page: pagination.current,
        limit: 10,
        ...filters
      };
      
      const response = await api.get('/api/admin/trips', { params });
      setTrips(response.data.trips);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Fetch trips error:', error);
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-4 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Trip Management</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Monitor and manage all trips on the platform</p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-4 sm:mb-6">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  placeholder="Search by origin or destination"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="open">Open</option>
                  <option value="booked">Booked</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <button
                onClick={() => setFilters({ status: '', search: '' })}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Trips List */}
          <div className="space-y-4">
            {trips.map((trip) => (
              <div key={trip._id} className="bg-white rounded-lg shadow-md p-4">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1 min-w-0">
                    {/* Rider Info */}
                    <div className="flex items-start mb-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <FaUser className="text-blue-600 text-sm" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-800 text-base sm:text-lg break-words">{trip.rider?.name}</h3>
                        <div className="text-xs sm:text-sm text-gray-600 break-words">
                          {trip.rider?.email}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600">
                          • {trip.rider?.phone}
                        </div>
                      </div>
                    </div>

                    {/* Route */}
                    <div className="mb-3 space-y-2">
                      <div className="flex items-start">
                        <FaMapMarkerAlt className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                        <span className="text-sm text-gray-700 break-words">{trip.origin?.address}</span>
                      </div>
                      <div className="flex items-start">
                        <FaMapMarkerAlt className="text-red-500 mr-2 mt-1 flex-shrink-0" />
                        <span className="text-sm text-gray-700 break-words">{trip.destination?.address}</span>
                      </div>
                    </div>

                    {/* Trip Details */}
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center text-sm text-gray-600">
                        <FaClock className="mr-2" />
                        <span>{new Date(trip.departureTime).toLocaleString('en-GB', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-semibold">₹{trip.price}</span> per seat
                      </div>
                      <div className="text-sm text-gray-600">
                        {trip.bookedSeats || 0}/{trip.availableSeats || 1} seats booked
                      </div>
                      <div className="text-sm text-gray-600">
                        Created: {new Date(trip.createdAt).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="mb-3">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                        trip.status === 'open' ? 'bg-blue-100 text-blue-800' :
                        trip.status === 'booked' ? 'bg-yellow-100 text-yellow-800' :
                        trip.status === 'in_progress' ? 'bg-green-100 text-green-800' :
                        trip.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {trip.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>

                    {/* Description */}
                    {trip.description && (
                      <div className="mb-4">
                        <p className="text-gray-600 text-sm">{trip.description}</p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="ml-4 sm:ml-6 flex flex-col space-y-2 flex-shrink-0">
                    <a
                      href={`/trip/${trip._id}`}
                      className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm flex items-center justify-center whitespace-nowrap"
                    >
                      <FaEye className="mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">View Details</span>
                      <span className="sm:hidden">View</span>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((pagination.current - 1) * 10) + 1} to {Math.min(pagination.current * 10, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
                  disabled={pagination.current === 1}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 py-2 text-sm text-gray-700">
                  Page {pagination.current} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
                  disabled={pagination.current === pagination.pages}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {trips.length === 0 && (
            <div className="text-center py-12">
              <FaMapMarkerAlt className="mx-auto text-gray-400 text-6xl mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No trips found
              </h3>
              <p className="text-gray-500">
                {filters.status || filters.search 
                  ? 'No trips match your current filters.'
                  : 'No trips have been created yet.'
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTrips;
